import React, { useMemo } from 'react';
import Button from 'src/Components/Button/Button';
import './UpgradePlan.css'
import { AuraProductLicenseType, IPaymentInfo, SuiteMapping } from 'src/Utils/Hooks/Payments/useGetPaymentProducts';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from 'src/Utils/CurrencySymbol/CurrencySymbol';
import { Annually, Monthly } from '../../LicensePayment';

interface IUpgradePlan {
    selectedTab: string;
    title: AuraProductLicenseType;
    currency: string;
    userSeats: number;
    storageInGb: number;
    monthlypriceObj: IPaymentInfo;
    yearlypriceObj: IPaymentInfo;
    onPayClick: (priceId: string) => void;
    disable: boolean;
    extraSeats: number;
    extraStorage: number;
    isCurrentPackage: boolean;
}

const UpgradePlan: React.FC<IUpgradePlan> = ({
    selectedTab,
    title,
    currency,
    userSeats,
    storageInGb,
    monthlypriceObj,
    yearlypriceObj,
    onPayClick,
    disable,
    extraSeats,
    extraStorage,
    isCurrentPackage,
}) => {
    const { t } = useTranslation();

    const annualPrice = useMemo(() => monthlypriceObj.price * 12, [monthlypriceObj.price]);

    const selectedPrice = useMemo(() => {
        return selectedTab === Monthly ? monthlypriceObj.price : yearlypriceObj.price;
    }, [selectedTab, monthlypriceObj.price, yearlypriceObj.price]);
    
    const billingPeriodText = useMemo(() => {
        return selectedTab === Monthly ? t('PerMonth') : t('PerYear');
    }, [selectedTab, t]);
    
    const selectedPriceId = useMemo(() => {
        return selectedTab === Monthly ? monthlypriceObj.priceId : yearlypriceObj.priceId;
    }, [selectedTab, monthlypriceObj.priceId, yearlypriceObj.priceId]);
    
    return (
        <div className={`col-sm-4 column`}>
            <div className="d-flex flex-column">
                <div className={`upgrade-plan__header d-flex flex-column justify-content-center align-items-center p-3 ${disable ? 'disabled' : ''}`}>
                    <span className="fw-bold">{t('AuraSoftwareSuite')}</span>
                    <span className="upgrade-plan__title">
                        {SuiteMapping[title]}
                    </span>
                </div>
                <div className={`upgrade-plan__pricing d-flex flex-column justify-content-center align-items-center p-3 ${disable ? 'disabled' : ''}`}>
                    {selectedTab === Annually && (
                        <del className="upgrade-plan__original-price">
                            {annualPrice}{getCurrencySymbol(currency)}
                        </del>
                    )}
                    <span className="upgrade-plan__price">
                        {selectedPrice}{getCurrencySymbol(currency)}
                    </span>
                    <span className="upgrade-plan__billing-period">
                        {billingPeriodText}
                    </span>
                </div>
                <div className={`upgrade-plan__feature d-flex flex-column justify-content-center align-items-center p-3 ${
                    extraSeats > 0 ? 'extraSeats' : ''
                } ${disable ? 'disabled' : ''}`}>
                    {
                        extraSeats > 0 ? (
                            <>
                                <span className="upgrade-plan__feature-value" style={{fontWeight: 'bold'}}>+{extraSeats}</span>
                                <span style={{fontWeight: 'bold'}}>{userSeats} {t('Total')}</span>
                            </>
                        ) : (
                            <span className="upgrade-plan__feature-value">{userSeats}</span>
                        )

                    }
                    <span className="upgrade-plan__feature-label">{t('FloatingSeats')}</span>
                </div>
                <div className={`upgrade-plan__feature d-flex flex-column justify-content-center align-items-center p-3 ${
                    extraStorage > 0 ? 'extraStorage' : ''
                } ${disable ? 'disabled' : ''}`}>
                    {
                        extraStorage > 0 ? (
                            <>
                                <span className="upgrade-plan__feature-value" style={{fontWeight: 'bold'}}>+ {extraStorage} GB</span>
                                <span style={{fontWeight: 'bold'}}>{storageInGb} GB {t('Total')}</span>
                            </>
                        ) : (
                            <span className="upgrade-plan__feature-value">{storageInGb} GB</span>
                        )
                    }
                    <span className="upgrade-plan__feature-label">{t('CloudStorage')}</span>
                </div>
                <div className="upgrade-plan__action d-flex flex-column justify-content-center align-items-center p-3">
                    {isCurrentPackage ? (
                        <span className="current-package-text">{t('YourCurrentPackage')}</span>
                    ) : (
                        <Button
                            width="100%"
                            text={t('UpgradeSuite', {
                                    suiteName: SuiteMapping[title],
                                })
                            }
                            onClick={() => onPayClick(selectedPriceId)}
                            disable={disable}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpgradePlan;
